import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../firebase/config';

// Collection name for MCP servers (matches your Firestore collection)
const MCP_COLLECTION = 'MCP-Servers';

// Fetch all MCP servers
export const getAllMCPServers = async () => {
  try {
    // Check if Firebase is available
    if (!db) {
      console.warn('Firebase not available - returning mock MCP servers for development');
      return getMockMCPServers();
    }

    const q = query(collection(db, MCP_COLLECTION));
    const querySnapshot = await getDocs(q);
    const servers = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Extract the actual name from the data, fallback to document ID if no name field
      const serverName = data.name || data.title || data.serverName || doc.id;
      
      // Extract tags from the actual tags field in Firestore, no auto-generation
      const serverTags = data.tags || data.categories || [];
      
      // Extract type from the actual type field, fallback to extracting from command
      const serverType = data.type || data.category || extractTypeFromCommand(data.command) || 'utility';
      
      servers.push({
        id: doc.id,
        name: serverName,
        description: data.description || 'No description available',
        author: data.author || 'Unknown',
        type: serverType,
        tags: Array.isArray(serverTags) ? serverTags : []
      });
    });
    
    return servers.sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error('Error fetching MCP servers:', error);
    console.warn('Falling back to mock data');
    return getMockMCPServers();
  }
};

// Mock MCP servers for development when Firebase is not available
const getMockMCPServers = () => {
  return [
    {
      id: 'filesystem-mcp',
      name: 'Filesystem MCP',
      description: 'Provides file system operations and directory browsing',
      author: 'ModelContextProtocol',
      type: 'filesystem',
      tags: ['filesystem', 'files', 'directory']
    },
    {
      id: 'github-mcp',
      name: 'GitHub MCP',
      description: 'Integration with GitHub repositories and issues',
      author: 'ModelContextProtocol',
      type: 'development',
      tags: ['github', 'git', 'repository']
    },
    {
      id: 'web-search-mcp',
      name: 'Web Search MCP',
      description: 'Performs web searches and retrieves content',
      author: 'ModelContextProtocol',
      type: 'search',
      tags: ['search', 'web', 'browser']
    },
    {
      id: 'database-mcp',
      name: 'Database MCP',
      description: 'Connect and query various databases',
      author: 'ModelContextProtocol',
      type: 'data',
      tags: ['database', 'sql', 'data']
    }
  ].sort((a, b) => a.name.localeCompare(b.name));
};

// Helper function to extract type from command (fallback only)
const extractTypeFromCommand = (command) => {
  if (!command) return 'unknown';
  
  const cmd = command.toLowerCase();
  if (cmd.includes('search') || cmd.includes('web')) return 'search';
  if (cmd.includes('file') || cmd.includes('fs')) return 'filesystem';
  if (cmd.includes('git') || cmd.includes('github')) return 'development';
  if (cmd.includes('database') || cmd.includes('sql')) return 'data';
  if (cmd.includes('ai') || cmd.includes('ml')) return 'ai';
  if (cmd.includes('chat') || cmd.includes('message')) return 'communication';
  if (cmd.includes('tool') || cmd.includes('utility')) return 'utility';
  
  return 'utility';
};

// Fetch MCP servers by type
export const getMCPServersByType = async (type) => {
  try {
    const allServers = await getAllMCPServers();
    return allServers.filter(server => server.type === type);
  } catch (error) {
    console.error('Error fetching MCP servers by type:', error);
    return [];
  }
};

// Fetch MCP servers by tags
export const getMCPServersByTag = async (tag) => {
  try {
    const allServers = await getAllMCPServers();
    return allServers.filter(server => 
      server.tags.some(serverTag => serverTag.toLowerCase() === tag.toLowerCase())
    );
  } catch (error) {
    console.error('Error fetching MCP servers by tag:', error);
    return [];
  }
};

// Search MCP servers by name or description
export const searchMCPServers = async (searchTerm) => {
  try {
    const allServers = await getAllMCPServers();
    const searchLower = searchTerm.toLowerCase();
    
    return allServers.filter(server =>
      server.name.toLowerCase().includes(searchLower) ||
      server.description.toLowerCase().includes(searchLower) ||
      server.author.toLowerCase().includes(searchLower) ||
      server.tags.some(tag => tag.toLowerCase().includes(searchLower)) ||
      (server.command && server.command.toLowerCase().includes(searchLower))
    );
  } catch (error) {
    console.error('Error searching MCP servers:', error);
    return [];
  }
};